<template>
  <div>
    <div :class="$style.container">
      <!-- 에러 상태 -->
      <div v-if="error" :class="$style.error">
        <p>{{ error }}</p>
        <button @click="fetchCommissions" :class="$style.retryButton">
          다시 시도
        </button>
      </div>

      <!-- 커미션 데이터 -->
      <div v-else>
        <!-- 검색 및 필터 -->
        <div :class="$style.controls">
          <!-- 카테고리 필터 -->
          <div :class="$style.typeFilter">
            <div :class="$style.typeDropdown" ref="categoryDropdownContainer">
              <button type="button" :class="$style.dropdownToggle" @click.stop="toggleCategoryDropdown"
                aria-label="카테고리 필터 메뉴">
                <img :class="$style.dehazeIcon" src="@/assets/images/dehaze.png" alt="" />
              </button>
              <div v-if="showCategoryDropdown" :class="$style.typeDropdownMenu">
                <button type="button" :class="[
                  $style.typeDropdownItem,
                  { [$style.activeTypeDropdownItem]: selectedCategory === 'all' },
                ]" @click="selectCategoryFromDropdown('all')">
                  전체
                </button>
                <button v-for="opt in categoryOptions" :key="'dd-' + opt.value" type="button" :class="[
                  $style.typeDropdownItem,
                  { [$style.activeTypeDropdownItem]: selectedCategory === opt.value },
                ]" @click="selectCategoryFromDropdown(opt.value)">
                  {{ opt.label }}
                </button>
              </div>
            </div>
            <div :class="$style.typeFilterScroll" ref="typeFilterScroll">
              <button :class="[$style.likeButton, { [$style.activeLike]: likedOnly }]" @click="handleLikeFilterClick"
                aria-label="찜한 항목만 보기">
                <img :class="$style.likeIcon" src="@/assets/images/heart.png" alt="찜" />
              </button>
              <button type="button" data-cat-pill="all" :class="[
                $style.typeButton,
                { [$style.activeType]: selectedCategory === 'all' },
              ]" @click="selectCategory('all')">
                전체
              </button>
              <button v-for="opt in categoryOptions" :key="opt.value" type="button" :data-cat-pill="opt.value" :class="[
                $style.typeButton,
                { [$style.activeType]: selectedCategory === opt.value },
              ]" @click="selectCategory(opt.value)">
                {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- 정렬 버튼 -->
          <div :class="$style.sortFilter">
            <!-- 날짜 필터 -->
            <div :class="$style.dateFilterWrapper">
              <button
                :class="[$style.dateFilterButton, { [$style.dateFilterActive]: dateFilterStart || dateFilterEnd }]"
                @click="toggleDateFilter">
                <img :class="$style.calendarIcon" src="@/assets/images/calendar_month.png" alt="기간 설정" />
              </button>
              <div v-if="showDateFilter" :class="$style.dateFilterDropdown">
                <div :class="$style.dateFilterBody">
                  <div :class="$style.dateFilterRow">
                    <label :class="$style.dateFilterLabel">시작</label>
                    <div :class="$style.customSelect" @click="toggleDropdown('startYear')">
                      <span :class="$style.customSelectText">{{ dateFilterStartYear ? dateFilterStartYear + '년' : '년도'
                      }}</span>
                      <span :class="$style.customSelectArrow">▼</span>
                      <div v-if="openDropdown === 'startYear'" :class="$style.customSelectOptions">
                        <div :class="$style.customSelectOption" @click.stop="selectYear('start', '')">년도</div>
                        <div v-for="year in availableYears" :key="'sy-' + year"
                          :class="[$style.customSelectOption, { [$style.selected]: dateFilterStartYear === year }]"
                          @click.stop="selectYear('start', year)">{{ year }}년</div>
                      </div>
                    </div>
                    <div :class="[$style.customSelect, { [$style.disabled]: !dateFilterStartYear }]"
                      @click="dateFilterStartYear && toggleDropdown('startMonth')">
                      <span :class="$style.customSelectText">{{ dateFilterStartMonth ? dateFilterStartMonth + '월' : '월'
                      }}</span>
                      <span :class="$style.customSelectArrow">▼</span>
                      <div v-if="openDropdown === 'startMonth'" :class="$style.customSelectOptions">
                        <div :class="$style.customSelectOption" @click.stop="selectMonth('start', '')">월</div>
                        <div v-for="month in availableStartMonths" :key="'sm-' + month"
                          :class="[$style.customSelectOption, { [$style.selected]: dateFilterStartMonth === month }]"
                          @click.stop="selectMonth('start', month)">{{ month }}월</div>
                      </div>
                    </div>
                  </div>
                  <div :class="$style.dateFilterRow">
                    <label :class="$style.dateFilterLabel">종료</label>
                    <div :class="$style.customSelect" @click="toggleDropdown('endYear')">
                      <span :class="$style.customSelectText">{{ dateFilterEndYear ? dateFilterEndYear + '년' : '년도'
                      }}</span>
                      <span :class="$style.customSelectArrow">▼</span>
                      <div v-if="openDropdown === 'endYear'" :class="$style.customSelectOptions">
                        <div :class="$style.customSelectOption" @click.stop="selectYear('end', '')">년도</div>
                        <div v-for="year in availableEndYears" :key="'ey-' + year"
                          :class="[$style.customSelectOption, { [$style.selected]: dateFilterEndYear === year }]"
                          @click.stop="selectYear('end', year)">{{ year }}년</div>
                      </div>
                    </div>
                    <div :class="[$style.customSelect, { [$style.disabled]: !dateFilterEndYear }]"
                      @click="dateFilterEndYear && toggleDropdown('endMonth')">
                      <span :class="$style.customSelectText">{{ dateFilterEndMonth ? dateFilterEndMonth + '월' : '월'
                      }}</span>
                      <span :class="$style.customSelectArrow">▼</span>
                      <div v-if="openDropdown === 'endMonth'" :class="$style.customSelectOptions">
                        <div :class="$style.customSelectOption" @click.stop="selectMonth('end', '')">월</div>
                        <div v-for="month in availableEndMonths" :key="'em-' + month"
                          :class="[$style.customSelectOption, { [$style.selected]: dateFilterEndMonth === month }]"
                          @click.stop="selectMonth('end', month)">{{ month }}월</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div :class="$style.dateFilterFooter">
                  <button :class="$style.dateFilterReset" @click="resetDateFilter">초기화</button>
                  <button :class="$style.dateFilterApply" @click="applyDateFilter">적용</button>
                </div>
              </div>
            </div>

            <div :class="$style.searchBox">
              <input v-model="searchQuery" type="text" placeholder="커미션명, 아티스트, 해시태그 검색" :class="$style.searchInput" />
            </div>

            <div :class="$style.filterBox">
              <button type="button" @click="toggleSort" :class="$style.sortButton">
                {{ sortLabel }}
                <img :class="$style.sortIcon" src="@/assets/images/swap_vert.png" alt="정렬 변경" />
              </button>
            </div>
          </div>
        </div>

        <!-- 로딩 상태 -->
        <div v-if="loading" :class="$style.loading">
          <div :class="$style.spinner"></div>
          <p>커미션 정보를 불러오는 중...</p>
        </div>

        <!-- 커미션 목록 -->
        <div v-else :class="$style.commissionList">
          <div v-if="paginatedCommissions.length === 0" :class="$style.emptyContainer">
            <p>등록된 커미션이 없습니다.</p>
          </div>
          <div v-else :class="$style.commissionGrid">
            <CommissionCard v-for="commission in paginatedCommissions" :key="commission.id" :commission="commission"
              :is-liked="isCommissionLiked(commission.id)" :show-like-button="true"
              :disabled="likingCommissionId === commission.id" @like-toggle="toggleCommissionLike" />
          </div>
        </div>

        <!-- 페이지네이션 -->
        <div v-if="!loading && totalPages > 1" :class="$style.pagination">
          <button :class="$style.pageButton" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
            ‹
          </button>

          <button v-for="page in pageRange" :key="page" :class="[
            $style.pageButton,
            { [$style.activePage]: page === currentPage },
          ]" @click="goToPage(page)">
            {{ page }}
          </button>

          <button :class="$style.pageButton" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
            ›
          </button>
        </div>
      </div>

      <!-- 플로팅 등록 버튼 -->
      <button v-if="userStore.isLogin" :class="$style.floatingButton" @click="openCommissionForm">
        <span>+ 신규등록</span>
      </button>

      <!-- 커미션 등록/수정 모달 -->
      <CommissionForm :is-visible="showCommissionForm" :mode="commissionFormMode"
        :commission-data="selectedCommissionData" @close-popup="closeCommissionForm" />
    </div>
  </div>
</template>

<script>
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { ref } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import { toggleCommissionLike as toggleCommissionLikeApi } from "@/utils/write/user";
import { getUserProfileImageFileName } from "@/utils/read/user";
import CommissionForm from "@/components/modal/form/CommissionForm.vue";
import CommissionCard from "@/components/CommissionCard.vue";

export default {
  name: "CommissionList",
  components: {
    CommissionForm,
    CommissionCard,
  },
  data() {
    return {
      commissions: [],
      loading: true,
      error: null,
      selectedCategory: "all",
      sortOption: "latest",
      categoryOptions: [
        { label: "디자인", value: "design" },
        { label: "일러스트", value: "illustration" },
        { label: "영상", value: "video" },
        { label: "음악", value: "music" },
        { label: "기타", value: "etc" },
      ],
      searchQuery: "",
      showCommissionForm: false,
      commissionFormMode: "create",
      selectedCommissionData: null,
      currentPage: 1,
      itemsPerPage: 10,
      userCommissionLikes: [],
      likingCommissionId: null,
      likedOnly: false,
      showDateFilter: false,
      dateFilterStartYear: "",
      dateFilterStartMonth: "",
      dateFilterEndYear: "",
      dateFilterEndMonth: "",
      dateFilterStart: null,
      dateFilterEnd: null,
      openDropdown: null,
      showCategoryDropdown: false,
    };
  },
  computed: {
    userStore() {
      return useUserStore();
    },
    sortLabel() {
      const map = { latest: "최신순", oldest: "오래된순", name: "이름순" };
      return map[this.sortOption] || "최신순";
    },
    filteredCommissions() {
      let list = this.commissions.slice();

      // 임시저장(draft)은 작성자 본인에게만 목록에 표시
      list = list.filter((c) => {
        const st = c.status || "published";
        if (st === "draft") {
          return this.userStore.isLogin && c.createdBy === this.userStore.email;
        }
        return true;
      });

      // 승인된 게시만 일반 노출 (approved 필드 없음 = 기존 데이터로 간주하여 공개)
      list = list.filter((c) => {
        const st = c.status || "published";
        if (st === "draft") return true;
        const isCreator =
          this.userStore.isLogin && c.createdBy === this.userStore.email;
        const publiclyOk = c.approved !== false;
        return publiclyOk || this.userStore.auth === "admin" || isCreator;
      });

      // 카테고리 필터
      if (this.selectedCategory !== "all") {
        list = list.filter((c) => c.category === this.selectedCategory);
      }

      // 찜 필터
      if (this.likedOnly) {
        list = list.filter((c) => this.userCommissionLikes.includes(c.id));
      }

      // 날짜 필터
      if (this.dateFilterStart || this.dateFilterEnd) {
        list = list.filter((c) => {
          const d = c.createdAt;
          if (!d) return false;
          if (this.dateFilterStart && d < this.dateFilterStart) return false;
          if (this.dateFilterEnd && d > this.dateFilterEnd) return false;
          return true;
        });
      }

      // 검색 필터 (커미션명 / 작가명 / 해시태그)
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().replace(/^#+/, "").toLowerCase();
        list = list.filter((c) => {
          const name = (c.name || "").toLowerCase();
          const nickname = (c.createdByNickname || "").toLowerCase();
          const tags = Array.isArray(c.hashtags) ? c.hashtags : [];
          return (
            name.includes(q) ||
            nickname.includes(q) ||
            tags.some((t) => String(t).toLowerCase().includes(q))
          );
        });
      }

      // 정렬
      if (this.sortOption === "latest") {
        list.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
      } else if (this.sortOption === "oldest") {
        list.sort((a, b) => (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0));
      } else if (this.sortOption === "name") {
        list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ko"));
      }

      return list;
    },
    availableYears() {
      const yearsSet = new Set();
      this.commissions.forEach((c) => {
        if (c.createdAt) yearsSet.add(c.createdAt.getFullYear());
      });
      return Array.from(yearsSet).sort((a, b) => b - a);
    },
    availableStartMonths() {
      if (!this.dateFilterStartYear) return [];
      const monthsSet = new Set();
      this.commissions.forEach((c) => {
        if (c.createdAt && c.createdAt.getFullYear() === this.dateFilterStartYear) {
          monthsSet.add(c.createdAt.getMonth() + 1);
        }
      });
      return Array.from(monthsSet).sort((a, b) => a - b);
    },
    availableEndYears() {
      let years = this.availableYears;
      if (this.dateFilterStartYear) {
        years = years.filter(y => y >= this.dateFilterStartYear);
      }
      return years;
    },
    availableEndMonths() {
      if (!this.dateFilterEndYear) return [];
      const monthsSet = new Set();
      this.commissions.forEach((c) => {
        if (c.createdAt && c.createdAt.getFullYear() === this.dateFilterEndYear) {
          monthsSet.add(c.createdAt.getMonth() + 1);
        }
      });
      let months = Array.from(monthsSet).sort((a, b) => a - b);
      if (this.dateFilterStartYear && this.dateFilterEndYear === this.dateFilterStartYear && this.dateFilterStartMonth) {
        months = months.filter(m => m >= this.dateFilterStartMonth);
      }
      return months;
    },
    paginatedCommissions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCommissions.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredCommissions.length / this.itemsPerPage);
    },
    pageRange() {
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 5) {
        const pages = [];
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
        return pages;
      }

      let start, end;

      if (current <= 3) {
        start = 1;
        end = 5;
      } else if (current >= total - 2) {
        start = total - 4;
        end = total;
      } else {
        start = current - 2;
        end = current + 2;
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    sortOption() {
      this.currentPage = 1;
    },
    selectedCategory() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.loadUserLikes();
    await this.fetchCommissions();
    document.addEventListener("click", this.handleCategoryDropdownClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleCategoryDropdownClickOutside);
  },
  methods: {
    async loadUserLikes() {
      if (!this.userStore.isLogin) {
        return;
      }

      try {
        const { getUserLikes } = await import("@/utils/read/user");
        const userLikes = await getUserLikes(this.userStore.email);
        this.userCommissionLikes = userLikes.commissionLikes || [];
      } catch (error) {
        console.error("사용자 찜 데이터 로드 오류:", error);
        this.userCommissionLikes = [];
      }
    },
    isCommissionLiked(commissionId) {
      return this.userCommissionLikes.includes(commissionId);
    },
    async toggleCommissionLike(commissionId) {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('likeFilter');
        return;
      }

      if (this.likingCommissionId) return;

      try {
        this.likingCommissionId = commissionId;
        const isCurrentlyLiked = this.isCommissionLiked(commissionId);

        const newLikedState = await toggleCommissionLikeApi(
          commissionId,
          this.userStore.email,
          isCurrentlyLiked
        );

        if (newLikedState) {
          this.userCommissionLikes.push(commissionId);
        } else {
          this.userCommissionLikes = this.userCommissionLikes.filter(id => id !== commissionId);
        }
      } catch (error) {
        console.error("커미션 찜 오류:", error);
        this.$alert("오류가 발생했습니다. 다시 시도해주세요.");
      } finally {
        this.likingCommissionId = null;
      }
    },
    async fetchCommissions() {
      try {
        this.loading = true;
        this.error = null;

        const commissionsQuery = query(
          collection(firestore, "commissions"),
          orderBy("createdAt", "desc")
        );
        const querySnapshot = await getDocs(commissionsQuery);

        const rawDocs = querySnapshot.docs.map((d) => ({ id: d.id, data: d.data() }));

        // 작성자 프로필 이미지 사전 로드 (이메일별 1회)
        const uniqueEmails = [...new Set(rawDocs.map(({ data }) => data.createdBy).filter(Boolean))];
        const profileMap = {};
        await Promise.all(
          uniqueEmails.map(async (email) => {
            try {
              const fileName = await getUserProfileImageFileName(email);
              if (!fileName) return;
              if (String(fileName).startsWith("http")) {
                profileMap[email] = fileName;
                return;
              }
              const imageRef = ref(storage, `profile/${fileName}`);
              profileMap[email] = await getDownloadURL(imageRef);
            } catch (err) {
              /* 프로필 로드 실패는 무시 */
            }
          })
        );

        this.commissions = await Promise.all(
          rawDocs.map(async ({ id, data }) => {
            let thumbnailUrl = data.thumbnail || "";

            // 썸네일 URL 처리
            if (thumbnailUrl && !thumbnailUrl.startsWith("http")) {
              try {
                const imageRef = ref(storage, `commission/${thumbnailUrl}`);
                thumbnailUrl = await getDownloadURL(imageRef);
              } catch (error) {
                console.error("커미션 썸네일 로드 실패:", error);
                thumbnailUrl = "";
              }
            }

            return {
              id,
              category: data.category || "",
              name: data.name || "",
              description: data.description || "",
              link: data.link || "",
              thumbnailUrl: thumbnailUrl,
              basePrice: Number(data.basePrice) || 0,
              hashtags: Array.isArray(data.hashtags) ? data.hashtags : [],
              createdAt: data.createdAt?.toDate() || new Date(),
              status: data.status || "published",
              approved: data.approved,
              createdBy: data.createdBy || "",
              createdByNickname: data.createdByNickname || "",
              createdByProfileUrl: profileMap[data.createdBy] || "",
            };
          })
        );
      } catch (error) {
        console.error("커미션 목록 로드 오류:", error);
        this.error = "커미션 목록을 불러오는 중 오류가 발생했습니다.";
        this.commissions = [];
      } finally {
        this.loading = false;
      }
    },
    openCommissionForm() {
      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        return;
      }
      this.commissionFormMode = "create";
      this.selectedCommissionData = null;
      this.showCommissionForm = true;
    },
    closeCommissionForm() {
      this.showCommissionForm = false;
      this.selectedCommissionData = null;
      this.fetchCommissions();
    },
    toggleSort() {
      const sortOptions = ["latest", "oldest", "name"];
      const currentIndex = sortOptions.indexOf(this.sortOption);
      const nextIndex = (currentIndex + 1) % sortOptions.length;
      this.sortOption = sortOptions[nextIndex];
    },
    selectCategory(cat) {
      this.selectedCategory = cat;
      this.$nextTick(() => {
        const container = this.$refs.typeFilterScroll;
        if (!container) return;
        const sel = cat === "all" ? '[data-cat-pill="all"]' : `[data-cat-pill="${cat}"]`;
        const activeBtn = container.querySelector(sel);
        if (activeBtn) this.scrollCategoryPillIntoView(activeBtn);
      });
    },
    toggleCategoryDropdown() {
      this.showCategoryDropdown = !this.showCategoryDropdown;
    },
    selectCategoryFromDropdown(cat) {
      this.selectCategory(cat);
      this.showCategoryDropdown = false;
    },
    handleCategoryDropdownClickOutside(event) {
      const el = this.$refs.categoryDropdownContainer;
      if (el && !el.contains(event.target)) {
        this.showCategoryDropdown = false;
      }
    },
    scrollCategoryPillIntoView(btn) {
      const container = this.$refs.typeFilterScroll;
      if (!container || !btn) return;
      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const scrollLeft =
        container.scrollLeft +
        (btnRect.left - containerRect.left) -
        containerRect.width / 2 +
        btnRect.width / 2;
      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    },
    handleLikeFilterClick() {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup("likeFilter");
        return;
      }
      this.likedOnly = !this.likedOnly;
    },
    toggleDateFilter() {
      this.showDateFilter = !this.showDateFilter;
      this.openDropdown = null;
    },
    toggleDropdown(dropdown) {
      this.openDropdown = this.openDropdown === dropdown ? null : dropdown;
    },
    selectYear(type, year) {
      if (type === "start") {
        this.dateFilterStartYear = year;
        if (!year) this.dateFilterStartMonth = "";
      } else {
        this.dateFilterEndYear = year;
        if (!year) this.dateFilterEndMonth = "";
      }
      this.openDropdown = null;
    },
    selectMonth(type, month) {
      if (type === "start") {
        this.dateFilterStartMonth = month;
      } else {
        this.dateFilterEndMonth = month;
      }
      this.openDropdown = null;
    },
    resetDateFilter() {
      this.dateFilterStartYear = "";
      this.dateFilterStartMonth = "";
      this.dateFilterEndYear = "";
      this.dateFilterEndMonth = "";
      this.dateFilterStart = null;
      this.dateFilterEnd = null;
      this.openDropdown = null;
      this.currentPage = 1;
      this.showDateFilter = false;
    },
    applyDateFilter() {
      if (this.dateFilterStartYear && this.dateFilterStartMonth) {
        this.dateFilterStart = new Date(this.dateFilterStartYear, this.dateFilterStartMonth - 1, 1);
      } else if (this.dateFilterStartYear) {
        this.dateFilterStart = new Date(this.dateFilterStartYear, 0, 1);
      } else {
        this.dateFilterStart = null;
      }
      if (this.dateFilterEndYear && this.dateFilterEndMonth) {
        this.dateFilterEnd = new Date(this.dateFilterEndYear, this.dateFilterEndMonth, 0, 23, 59, 59);
      } else if (this.dateFilterEndYear) {
        this.dateFilterEnd = new Date(this.dateFilterEndYear, 11, 31, 23, 59, 59);
      } else {
        this.dateFilterEnd = null;
      }
      this.currentPage = 1;
      this.showDateFilter = false;
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  },
};
</script>

<style module>
/* ========================================
   Base (> 480px)
   ======================================== */
.container {
  width: 90%;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.error {
  text-align: center;
  color: #dc3545;
  margin: 15px 0;
}

.retryButton {
  background: #66bdff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
  font-size: 13px;
}

.retryButton:hover {
  background: #4a9eff;
}

/* 로딩 상태 (ArtistList.vue와 동일 패턴) */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.loading p {
  margin: 0;
  font-size: 0.9375rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #66bdff;
  border-radius: 50%;
  animation: commissionListSpin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes commissionListSpin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 10px;
}

.searchBox {
  flex: 1;
  min-width: 100px;
}

.searchInput {
  width: 100%;
  height: 34px !important;
  padding: 0 12px;
  border: 1px solid rgba(102, 189, 255, 0.25);
  border-radius: 10px;
  font-size: 0.875rem;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background: rgba(255, 255, 255, 0.7);
}

.searchInput:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

.typeFilter {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  min-width: 0;
  margin-top: 8px;
}

.typeDropdown {
  position: relative;
  flex-shrink: 0;
}

.dropdownToggle {
  height: 34px;
  width: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(102, 189, 255, 0.35);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.dropdownToggle:hover {
  background: rgba(255, 255, 255, 0.95);
  border-color: rgba(102, 189, 255, 0.55);
}

.dehazeIcon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  opacity: 0.55;
}

.typeDropdownMenu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(102, 189, 255, 0.18);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(45, 120, 180, 0.12);
  z-index: 9999;
  min-width: 120px;
  max-height: min(320px, 50vh);
  overflow-y: auto;
  animation: typeDropdownSlide 0.2s ease;
}

@keyframes typeDropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.typeDropdownItem {
  width: 100%;
  padding: 10px 14px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 0.875rem;
  color: #5a6b7a;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  border-bottom: 1px solid rgba(102, 189, 255, 0.1);
}

.typeDropdownItem:last-child {
  border-bottom: none;
}

.typeDropdownItem:hover {
  background: rgba(102, 189, 255, 0.08);
  color: #1a3a52;
}

.activeTypeDropdownItem {
  background: linear-gradient(135deg, #66bdff 0%, #4a9eff 100%);
  color: white;
  font-weight: bold;
}

.typeFilterScroll {
  display: flex;
  gap: 6px;
  align-items: center;
  overflow-x: auto;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.typeFilterScroll::-webkit-scrollbar {
  display: none;
}

.likeButton {
  padding: 5px 7px;
  border: 1px solid rgba(102, 189, 255, 0.3);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.likeButton:hover {
  background: #f8f9fa;
  border-color: #ff6b9d;
}

.likeButton:active {
  background: #ff6b9d;
  border-color: #ff6b9d;
}

.activeLike {
  background: #ff6b9d;
  color: white;
  border-color: #ff6b9d;
}

.activeLike:hover {
  background: #ff6b9d;
  border-color: #ff6b9d;
}

.likeIcon {
  width: 20px;
  height: 20px;
  margin-top: 3px;
}

.typeButton {
  padding: 8px 16px;
  border: 1px solid rgba(102, 189, 255, 0.3);
  background: rgba(255, 255, 255, 0.7);
  color: #4a7a9a;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.875rem;
  font-family: "NanumSquareRoundB", sans-serif;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.typeButton.activeType {
  background: #66bdff;
  color: white;
  border-color: #66bdff;
}

.registerButton {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #66bdff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundB", sans-serif;
}

.registerButton:hover {
  background: #4a9eff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 189, 255, 0.3);
}

.plusIcon {
  width: 14px;
  height: 14px;
  filter: brightness(0) invert(1);
}

.desktopOnly {
  display: flex;
}

.mobileOnly {
  display: none;
}

/* 정렬 버튼 — VideoList.vue와 동일 레이아웃·타이포, 색상만 커미션 블루 */
.sortButton {
  padding: 8px 12px;
  background: #66bdff;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.875rem;
  font-family: "NanumSquareRoundB", sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.sortButton:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 189, 255, 0.3);
}

.sortFilter {
  width: 100%;
  display: flex;
  gap: 6px;
}

.sortIcon {
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
}

.filterBox {
  flex-shrink: 0;
}

.commissionList {
  width: 100%;
}

.emptyContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 50px 15px;
  text-align: center;
  color: #666;
  font-size: 15px;
}

.commissionGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;
  margin-bottom: 80px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 25px;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 80px;
}

.pageButton {
  padding: 7px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #ffffff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 32px;
  text-align: center;
}

.pageButton:hover:not(.activePage):not(:disabled) {
  background: #f8f9fa;
  border-color: #ccc;
  color: #333;
}

.pageButton:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  color: #ccc;
  background: #f8f9fa;
}

.activePage {
  background: #222;
  color: #ffffff;
  border-color: #222;
}

/* 날짜 필터 */
.dateFilterWrapper {
  position: relative;
  flex-shrink: 0;
}

.dateFilterButton {
  width: 36px;
  height: 35px;
  padding: 5px;
  border: 1px solid rgba(102, 189, 255, 0.3);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.dateFilterButton:active {
  border-color: rgba(102, 189, 255, 0.55);
}

.dateFilterActive {
  background: linear-gradient(135deg, #66bdff 0%, #4a9eff 100%);
  border-color: transparent;
}

.dateFilterActive .calendarIcon {
  filter: brightness(0) invert(1);
}

.calendarIcon {
  width: 20px;
  height: 20px;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.dateFilterButton:hover .calendarIcon {
  opacity: 0.8;
}

.dateFilterDropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 260px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(102, 189, 255, 0.2);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(45, 120, 180, 0.12);
  z-index: 100;
  overflow: visible;
}

.dateFilterBody {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dateFilterRow {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dateFilterLabel {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 0.75rem;
  color: #666;
  width: 32px;
  flex-shrink: 0;
}

/* 커스텀 드롭다운 */
.customSelect {
  flex: 1;
  position: relative;
  padding: 7px 8px;
  border: 1px solid rgba(102, 189, 255, 0.3);
  border-radius: 8px;
  font-size: 0.75rem;
  font-family: "NanumSquareRoundR", sans-serif;
  background: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: border-color 0.2s ease;
  user-select: none;
}

.customSelect:hover {
  border-color: rgba(102, 189, 255, 0.55);
}

.customSelect.disabled {
  background: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.customSelectText {
  color: #333;
}

.customSelect.disabled .customSelectText {
  color: #999;
}

.customSelectArrow {
  font-size: 0.625rem;
  color: #999;
  margin-left: 4px;
}

.customSelectOptions {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(102, 189, 255, 0.2);
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(45, 120, 180, 0.1);
  max-height: 150px;
  overflow-y: auto;
  z-index: 10;
}

.customSelectOption {
  padding: 7px 8px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.customSelectOption:hover {
  background: rgba(102, 189, 255, 0.1);
}

.customSelectOption.selected {
  background: linear-gradient(135deg, #66bdff 0%, #4a9eff 100%);
  color: white;
}

.customSelectOption.selected:hover {
  background: linear-gradient(135deg, #4a9eff 0%, #2f8eff 100%);
}

.dateFilterFooter {
  display: flex;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid rgba(102, 189, 255, 0.1);
  background: rgba(238, 246, 255, 0.6);
  border-radius: 0 0 14px 14px;
}

.dateFilterReset {
  flex: 1;
  padding: 9px;
  border: 1px solid rgba(102, 189, 255, 0.3);
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 0.75rem;
  color: #4a7a9a;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dateFilterReset:hover {
  border-color: rgba(102, 189, 255, 0.5);
  background: rgba(255, 255, 255, 1);
}

.dateFilterApply {
  flex: 1;
  padding: 9px;
  border: none;
  background: linear-gradient(135deg, #66bdff 0%, #4a9eff 100%);
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 0.75rem;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dateFilterApply:hover {
  opacity: 0.95;
  transform: translateY(-1px);
}

/* 플로팅 등록 버튼 */
.floatingButton {
  position: fixed;
  right: calc(50% - 500px);
  max-width: 1024px;
  margin-right: 12px;
  height: 46px;
  padding: 0 12px;
  border-radius: 30px;
  background: #66bdff;
  color: white;
  border: none;
  box-shadow: 0 4px 16px rgba(102, 189, 255, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 199;
  font-size: 1rem;
  font-weight: bold;
  font-family: "NanumSquareRoundB", sans-serif !important;
  transition: all 0.3s ease;
  bottom: 80px !important;
}

@media screen and (max-width: 1024px) {
  .floatingButton {
    right: 12px;
  }
}

.floatingButton:hover {
  background: #4a9eff;
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(102, 189, 255, 0.6);
}

.floatingButton:active {
  transform: scale(0.95);
}

/* ========================================
   Mobile (~480px)
   ======================================== */
@media (max-width: 480px) {
  .container {
    box-sizing: border-box;
  }

  .controls {
    gap: 8px;
    padding: 10px 0;
  }

  .typeFilter {
    gap: 6px;
    margin-top: 0;
  }

  .typeFilterScroll {
    gap: 5px;
  }

  .dropdownToggle {
    height: 28px;
    width: 28px;
    border-radius: 6px;
  }

  .dehazeIcon {
    width: 18px;
    height: 18px;
  }

  .typeDropdownMenu {
    min-width: 110px;
    max-height: min(280px, 45vh);
    top: calc(100% + 6px);
  }

  .typeDropdownItem {
    padding: 8px 12px;
    font-size: 0.8125rem;
  }

  .typeButton {
    padding: 6px 12px;
    font-size: 0.75rem;
  }

  .likeButton {
    padding: 4px 6px;
  }

  .likeIcon {
    width: 18px;
    height: 18px;
  }

  .dateFilterButton {
    width: 31px;
    height: 31px;
    padding: 4px;
  }

  .calendarIcon {
    width: 20px;
    height: 20px;
  }

  .dateFilterDropdown {
    width: calc(100vw - 32px);
    max-width: 280px;
    left: 0;
  }

  .dateFilterBody {
    padding: 10px;
    gap: 8px;
  }

  .dateFilterRow {
    gap: 6px;
  }

  .dateFilterLabel {
    font-size: 0.6875rem;
    width: 28px;
  }

  .customSelect {
    padding: 6px 6px;
    font-size: 0.6875rem;
  }

  .customSelectOption {
    padding: 6px 8px;
    font-size: 0.6875rem;
  }

  .dateFilterFooter {
    padding: 8px 10px;
    gap: 6px;
  }

  .dateFilterReset,
  .dateFilterApply {
    padding: 8px;
    font-size: 0.6875rem;
  }


  .desktopOnly {
    display: none;
  }

  .searchBox {
    min-width: 80px;
  }

  .searchInput {
    height: 31px !important;
    font-size: 0.75rem;
    padding: 0 10px;
  }

  .filterBox {
    flex-shrink: 0;
  }

  .sortButton {
    padding: 8px 10px;
    font-size: 0.75rem;
  }

  .sortButton:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 189, 255, 0.3);
  }

  .sortIcon {
    width: 14px;
    height: 14px;
  }

  .commissionGrid {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-bottom: 70px;
  }

  .emptyContainer {
    padding: 40px 10px;
    font-size: 14px;
  }

  .pagination {
    gap: 4px;
    margin-top: 20px;
    margin-bottom: 70px;
  }

  .pageButton {
    padding: 5px 8px;
    font-size: 11px;
    min-width: 26px;
    border-radius: 4px;
  }

  .error {
    margin: 12px 0;
    font-size: 14px;
  }

  .retryButton {
    padding: 8px 14px;
    font-size: 12px;
    border-radius: 4px;
  }
}
</style>
